// convex/lib/config/domains/operations.ts

import { validatorPersonnelRole } from "../../../companies/operations/personnel_roles";
import { validatorPersonnel } from "../../../companies/operations/personnel";
import { validatorEquipment } from "../../../companies/operations/equipment";
import { validatorWorkOrder } from "../../../companies/operations/work_orders";
import { validatorWorkOrderPersonnel } from "../../../companies/operations/work_order_personnel";
import { validatorWorkOrderEquipment } from "../../../companies/operations/work_order_equipment";
import { validatorWorkOrderExpense } from "../../../companies/operations/work_order_expenses";
import { validatorWorkOrderLog } from "../../../companies/operations/work_order_logs";
import { validatorWorkOrderService } from "../../../companies/operations/work_order_services";
import { validatorWorkOrderProduct } from "../../../companies/operations/work_order_products";
import { validatorClient } from "../../../companies/clients";
import { validatorProductService } from "../../../companies/products";
import { validatorUsers } from "../../../system/users";
import type { DomainGroup, TableScope } from "../types";

// ============================================================================
// OPERATIONS DOMAIN - Status Values for i18n
// ============================================================================

export const WORK_ORDER_STATUSES = [
  "pending",
  "in_progress",
  "completed",
  "cancelled",
  "on_hold",
] as const;
export const PERSONNEL_STATUSES = ["active", "inactive"] as const;
export const EQUIPMENT_STATUSES = [
  "operational",
  "maintenance",
  "retired",
] as const;

export const operationsDomain: DomainGroup = {
  id: "operations",
  // Joya (Tier 2): excluida del bundle on-prem. Solo cloud (SaaS/Desktop).
  editions: ["saas", "desktop"],
  name: "Operaciones",
  description: "Gestión de operaciones, órdenes de trabajo y recursos",
  icon: "Wrench",
  order: 8,
  status: "active",
  // Acceso por defecto de company_user a este grupo (ver default_permissions.ts)
  defaultAccess: { company_user: "full" },

  // ── Configuración i18n del dominio ────────────────────
  i18n: {
    namespace: "operations",
    keys: {
      title: "operations.title",
      description: "operations.description",
    },
  },

  modules: [
    {
      id: "work_orders",
      name: "Órdenes de Trabajo",
      description: "Creación y seguimiento de órdenes de trabajo",
      icon: "Wrench",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/operations/work-orders"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 14,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "operations.work_orders",
        keys: {
          title: "operations.work_orders.title",
          description: "operations.work_orders.description",
          longDescription: "operations.work_orders.longDescription",
          searchPlaceholder: "operations.work_orders.searchPlaceholder",
          emptyState: "operations.work_orders.emptyState",
          actions: {
            create: "operations.work_orders.actions.create",
            edit: "operations.work_orders.actions.edit",
            delete: "operations.work_orders.actions.delete",
            view: "operations.work_orders.actions.view",
          },
        },
        translatableFields: ["title", "description"],
        translatableEnums: [{ field: "status", values: WORK_ORDER_STATUSES }],
      },

      tables: [
        {
          tableName: "work_orders",
          schema: validatorWorkOrder,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "client",
              sourceField: "clientId",
              schema: validatorClient,
              tableName: "clients",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["title", "code"],
          hasStatusIndex: true,
          customRules: [],
          searchIndex: {
            name: "search_work_orders",
            field: "title",
          },
        },
        {
          tableName: "work_order_personnel",
          schema: validatorWorkOrderPersonnel,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "workOrder",
              sourceField: "workOrderId",
              schema: validatorWorkOrder,
              tableName: "work_orders",
            },
            {
              key: "personnel",
              sourceField: "personnelId",
              schema: validatorPersonnel,
              tableName: "personnel",
            },
          ],
          searchFields: [],
          hasStatusIndex: false,
          customRules: [],
        },
        {
          tableName: "work_order_equipment",
          schema: validatorWorkOrderEquipment,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "workOrder",
              sourceField: "workOrderId",
              schema: validatorWorkOrder,
              tableName: "work_orders",
            },
            {
              key: "equipment",
              sourceField: "equipmentId",
              schema: validatorEquipment,
              tableName: "equipment",
            },
          ],
          searchFields: [],
          hasStatusIndex: false,
          customRules: [],
        },
        {
          tableName: "work_order_expenses",
          schema: validatorWorkOrderExpense,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "workOrder",
              sourceField: "workOrderId",
              schema: validatorWorkOrder,
              tableName: "work_orders",
            },
            {
              key: "product",
              sourceField: "productId",
              schema: validatorProductService,
              tableName: "products",
            },
          ],
          searchFields: ["description"],
          hasStatusIndex: false,
          customRules: [],
        },
        {
          tableName: "work_order_logs",
          schema: validatorWorkOrderLog,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "workOrder",
              sourceField: "workOrderId",
              schema: validatorWorkOrder,
              tableName: "work_orders",
            },
            {
              key: "recordedByUser",
              sourceField: "recordedBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["description"],
          hasStatusIndex: false,
          customRules: [],
        },
        {
          tableName: "work_order_services",
          schema: validatorWorkOrderService,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "workOrder",
              sourceField: "workOrderId",
              schema: validatorWorkOrder,
              tableName: "work_orders",
            },
          ],
          searchFields: ["name", "description"],
          hasStatusIndex: false,
          customRules: [],
        },
        {
          tableName: "work_order_products",
          schema: validatorWorkOrderProduct,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "workOrder",
              sourceField: "workOrderId",
              schema: validatorWorkOrder,
              tableName: "work_orders",
            },
          ],
          searchFields: ["name", "description"],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [
        { tableName: "clients" },
        { tableName: "personnel" },
        { tableName: "equipment" },
        { tableName: "products" },
      ],
    },
    {
      id: "personnel_roles",
      name: "Roles de Personal",
      description: "Configuracion de roles y cargos del personal operativo",
      icon: "UserCog",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/operations/personnel-roles"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 16,
        status: "active",
      },
      i18n: {
        namespace: "operations.personnel_roles",
        keys: {
          title: "operations.personnel_roles.title",
          description: "operations.personnel_roles.description",
          longDescription: "operations.personnel_roles.longDescription",
          searchPlaceholder: "operations.personnel_roles.searchPlaceholder",
          emptyState: "operations.personnel_roles.emptyState",
          actions: {
            edit: "operations.personnel_roles.actions.edit",
            view: "operations.personnel_roles.actions.view",
          },
        },
        translatableFields: ["name"],
        translatableEnums: [],
      },
      tables: [
        {
          tableName: "personnel_roles",
          schema: validatorPersonnelRole,
          scope: "company" as TableScope,
          masterData: true,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["name"],
          hasStatusIndex: true,
          customRules: [],
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "personnel",
      name: "Personal",
      description: "Gestión de personal y roles operacionales",
      icon: "Users",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/operations/personnel"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 15,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "operations.personnel",
        keys: {
          title: "operations.personnel.title",
          description: "operations.personnel.description",
          longDescription: "operations.personnel.longDescription",
          searchPlaceholder: "operations.personnel.searchPlaceholder",
          emptyState: "operations.personnel.emptyState",
          actions: {
            create: "operations.personnel.actions.create",
            edit: "operations.personnel.actions.edit",
            delete: "operations.personnel.actions.delete",
            view: "operations.personnel.actions.view",
          },
        },
        translatableFields: ["name"],
        translatableEnums: [{ field: "status", values: PERSONNEL_STATUSES }],
      },

      tables: [
        {
          tableName: "personnel",
          schema: validatorPersonnel,
          scope: "company" as TableScope,
          masterData: true,
          relations: [
            {
              key: "role",
              sourceField: "roleId",
              schema: validatorPersonnelRole,
              tableName: "personnel_roles",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["name"],
          hasStatusIndex: true,
          customRules: [],
          searchIndex: {
            name: "search_personnel",
            field: "name",
          },
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "equipment",
      name: "Equipos",
      description: "Gestión de equipos y recursos operacionales",
      icon: "Zap",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/operations/equipment"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 16,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "operations.equipment",
        keys: {
          title: "operations.equipment.title",
          description: "operations.equipment.description",
          longDescription: "operations.equipment.longDescription",
          searchPlaceholder: "operations.equipment.searchPlaceholder",
          emptyState: "operations.equipment.emptyState",
          actions: {
            create: "operations.equipment.actions.create",
            edit: "operations.equipment.actions.edit",
            delete: "operations.equipment.actions.delete",
            view: "operations.equipment.actions.view",
          },
        },
        translatableFields: ["name"],
        translatableEnums: [{ field: "status", values: EQUIPMENT_STATUSES }],
      },

      tables: [
        {
          tableName: "equipment",
          schema: validatorEquipment,
          scope: "company" as TableScope,
          masterData: true,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["name", "identifier"],
          hasStatusIndex: true,
          customRules: [],
          searchIndex: {
            name: "search_equipment",
            field: "name",
          },
        },
      ],
      readOnlyTables: [],
    },
  ],
};
